import { useEffect, useState } from 'react';
import { Clock, MessageSquareOff, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { StarRating } from '@/components/ui/StarRating';
import { cn } from '@/lib/cn';
import type { ReviewView } from '@/types/api';

/**
 * ReviewForm — the in-modal star + comment editor.
 *
 * State is owned locally so unsaved edits don't bubble out until the
 * user clicks Save. The form prefills from `existing` when present
 * (the reader's own prior review); we re-sync local state when the
 * `existing` reference changes (e.g. after a save returns the
 * canonical row, or the user picks a different reader).
 *
 * `commentsEnabled` is the family-level toggle: when false, the
 * textarea is replaced with a small inline notice and the submit
 * button only carries the rating. Submitting still works — the user
 * can rate even with comments off.
 *
 * The "awaiting approval" banner appears when the persisted comment
 * status is 'pending' (newly submitted or edited). Rejected comments
 * show a quieter notice — the user can re-edit to resubmit.
 */
export interface ReviewFormProps {
  existing: ReviewView | null;
  loading: boolean;
  disabled: boolean;
  commentsEnabled: boolean;
  onSubmit: (params: { rating: number; commentText: string }) => Promise<void> | void;
  onDelete?: () => Promise<void> | void;
}

const MAX_COMMENT_LENGTH = 1000;

export function ReviewForm({
  existing,
  loading,
  disabled,
  commentsEnabled,
  onSubmit,
  onDelete,
}: ReviewFormProps) {
  const [rating, setRating] = useState(0);
  const [commentText, setCommentText] = useState('');

  // Re-sync local state whenever the canonical review row changes (a
  // save returned new server values, or a reader swap brought in a
  // different review). Treating `existing` as the source of truth
  // here keeps the optimistic-vs-server diff from going stale.
  useEffect(() => {
    setRating(existing?.rating ?? 0);
    setCommentText(existing?.commentText ?? '');
  }, [existing?.id, existing?.updatedAt]);

  const canSubmit = rating > 0 && !disabled && !loading;
  const trimmedComment = commentText.trim();
  const commentDirty = (existing?.commentText ?? '') !== trimmedComment;
  const ratingDirty = (existing?.rating ?? 0) !== rating;
  const dirty = ratingDirty || commentDirty;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    // When comments are disabled at the family level, never send text.
    const text = commentsEnabled ? trimmedComment : '';
    await onSubmit({ rating, commentText: text });
  };

  // Status banner: shows what's happening with the comment portion.
  // Skipped when the review is ratings-only.
  const status = existing?.commentStatus ?? null;

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div>
        <div className="or-kicker mb-2">Your review</div>
        <StarRating
          value={rating}
          onChange={setRating}
          size="lg"
          label="Rate this book"
        />
        {rating === 0 ? (
          <div className="mt-1.5 text-[12px] text-or-slate-500">Pick a star to rate.</div>
        ) : null}
      </div>

      {commentsEnabled ? (
        <div>
          <label
            htmlFor="review-comment"
            className="or-kicker mb-1.5 block text-or-slate-700"
          >
            Add a comment (optional)
          </label>
          <textarea
            id="review-comment"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value.slice(0, MAX_COMMENT_LENGTH))}
            maxLength={MAX_COMMENT_LENGTH}
            rows={3}
            disabled={disabled || loading}
            placeholder="Share what you liked, or what surprised you…"
            className={cn(
              'w-full rounded-xl border border-or-slate-200 bg-white px-3 py-2 text-[14px] leading-relaxed text-or-slate-800 shadow-sm',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-orange-500/40',
            )}
          />
          <div className="mt-1 flex flex-col gap-0.5 text-[11px] text-or-slate-500">
            <span>
              {commentText.length}/{MAX_COMMENT_LENGTH}
            </span>
            <span>Comments are reviewed by a grown-up before showing up.</span>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-2 rounded-xl border border-or-slate-100 bg-white p-3 text-[12px] text-or-slate-600">
          <MessageSquareOff className="mt-0.5 h-3.5 w-3.5 shrink-0 text-or-slate-400" aria-hidden />
          <span>
            Comments are turned off for this account, so only your star rating will be saved.
          </span>
        </div>
      )}

      {/* Status banner — only meaningful when the existing review has
          comment text. Pending/rejected/approved each map to a tone. */}
      {status === 'pending' ? (
        <div className="flex items-start gap-2 rounded-xl border border-or-orange-200 bg-or-orange-50 px-3 py-2 text-[12px] text-or-orange-800">
          <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>Your comment is awaiting approval. Your star rating is already live.</span>
        </div>
      ) : status === 'rejected' ? (
        <div className="rounded-xl border border-or-rose-100 bg-or-rose-50 px-3 py-2 text-[12px] text-or-rose-700">
          A grown-up didn't approve this comment. Edit and resubmit, or remove it.
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button type="submit" disabled={!canSubmit || !dirty} size="md">
          {existing ? 'Update review' : 'Submit review'}
        </Button>
        {onDelete && existing ? (
          <Button
            type="button"
            variant="danger"
            size="sm"
            onClick={() => void onDelete()}
            disabled={disabled || loading}
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
            Remove
          </Button>
        ) : null}
      </div>
    </form>
  );
}
